import { ThemeProvider } from "@mui/material/styles";
import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route } from "react-router-dom";
import CaseManagementGate from "src/features/student-cases/components/CaseManagementGate";
import GlobalCallLayer from "src/pages/Admin/SuccessQueue/components/GlobalCallLayer";
import ClientScreenPreloader from "src/pages/ClientScreen/ClientScreenPreloader";
import loadClientScreen from "src/pages/ClientScreen/loadClientScreen";
import { successQueueTheme } from "src/theme";
import AppSkeleton from "../pages/AppSkeleton";
import SessionBoundary from "../SessionBoundary";
import {
  AdminThemeLayout,
  ADMIN_PATH,
  IMPACT_REPORT_PATH,
} from "../themeLayouts";
import { UserType } from "../types/User";

const ClientScreen = lazy(loadClientScreen);
const AdminScreen = lazy(() => import("src/pages/AdminScreen/AdminScreen"));
const CohortScreen = lazy(
  () => import("src/pages/Admin/CohortScreen/CohortScreen")
);
const SuperAdminScreen = lazy(
  () => import("src/pages/Admin/SuperAdminScreen/SuperAdminScreen")
);
const CohortSelectScreen = lazy(
  () => import("src/pages/Admin/CohortScreen/CohortSelectScreen")
);
const NewVocationsScreen = lazy(
  () => import("src/pages/Admin/NewVocations/NewVocationsScreen")
);
const JobSearchAnalyticsScreen = lazy(
  () => import("src/pages/Admin/JobSearchAnalytics/JobSearchAnalyticsScreen")
);
const SalesEnablementScreen = lazy(
  () => import("src/pages/Admin/SalesEnablement/SalesEnablementScreen")
);
const SkillsTrainingAnalyticsScreen = lazy(
  () =>
    import(
      "src/pages/Admin/SkillsTrainingAnalytics/SkillsTrainingAnalyticsScreen"
    )
);
const OnlineTrainingAnalyticsScreen = lazy(
  () =>
    import(
      "src/pages/Admin/OnlineTrainingAnalytics/OnlineTrainingAnalyticsScreen"
    )
);
const ContentDashboardScreen = lazy(
  () => import("src/pages/Admin/ContentDashboard/ContentDashboardScreen")
);
const CurriculumScreen = lazy(
  () => import("src/pages/Admin/Curriculum/CurriculumScreen")
);
const ChangelogScreen = lazy(
  () => import("src/pages/Admin/ChangelogScreen/ChangelogScreen")
);
const SuccessQueueScreen = lazy(
  () => import("src/pages/Admin/SuccessQueue/SuccessQueueScreen")
);
const OutreachDialScreen = lazy(
  () => import("src/pages/Admin/OutreachDial/OutreachDialScreen")
);
const FreeDialScreen = lazy(
  () => import("src/pages/Admin/FreeDial/FreeDialScreen")
);
const ReimbursementAnalyticsScreen = lazy(
  () =>
    import(
      "src/pages/Admin/ReimbursementAnalytics/ReimbursementAnalyticsScreen"
    )
);
const ReimbursementAIAnalysisScreen = lazy(
  () =>
    import(
      "src/pages/Admin/ReimbursementAnalytics/ReimbursementAIAnalysisScreen"
    )
);
const DocumentAIAnalysisScreen = lazy(
  () => import("src/pages/Admin/DocumentAIAnalytics/DocumentAIAnalysisScreen")
);
const ReimbursementReviewScreen = lazy(
  () => import("src/pages/Admin/ReimbursementReviewScreen")
);
const FinancialAidReviewScreen = lazy(
  () => import("src/pages/Admin/FinancialAidReviewScreen")
);
const FinancialAidAnalyticsScreen = lazy(
  () =>
    import("src/pages/Admin/FinancialAidAnalytics/FinancialAidAnalyticsScreen")
);
const BillingScreen = lazy(
  () => import("src/pages/Admin/BillingScreen/BillingScreen")
);
const AdminLayout = lazy(() => import("src/pages/AdminScreen/AdminLayout"));
const AdminReferralsScreen = lazy(
  () => import("src/pages/AdminScreen/AdminReferralsScreen")
);
const MenuLayout = lazy(
  () => import("src/shared/components/Navigation/MenuLayout")
);
const ImpactReportScreen = lazy(() => import("src/pages/ImpactReportScreen"));
const MediaLibraryScreen = lazy(
  () => import("src/pages/Admin/MediaLibraryScreen/MediaLibraryScreen")
);
const ScoreboardScreen = lazy(
  () => import("src/pages/Admin/Scoreboard/ScoreboardScreen")
);
const AiConsolePage = lazy(() => import("src/pages/Admin/AiConsole"));

function AdminSearchRoute(): JSX.Element {
  return (
    <ThemeProvider theme={successQueueTheme}>
      <MenuLayout currentTab="clients">
        <AdminLayout>
          <AdminScreen />
        </AdminLayout>
      </MenuLayout>
    </ThemeProvider>
  );
}

export function adminRoutes(): JSX.Element {
  return (
    <Route element={<AdminThemeLayout />}>
      <Route
        path={IMPACT_REPORT_PATH}
        element={<Navigate to={`${IMPACT_REPORT_PATH}/mocj`} replace />}
      />
      <Route
        path={`${IMPACT_REPORT_PATH}/:dashboardId`}
        element={
          <ThemeProvider theme={successQueueTheme}>
            <Suspense fallback={<AppSkeleton />}>
              <ImpactReportScreen />
            </Suspense>
          </ThemeProvider>
        }
      />
      <Route
        path={ADMIN_PATH}
        element={
          <SessionBoundary userType={UserType.Admin}>
            <Suspense fallback={<AppSkeleton />}>
              <>
                <ClientScreenPreloader />
                {/* Mounts the in-app phone for Dial coaches around every admin
                    page, so inbound calls ring wherever they are (see
                    GlobalCallLayer). Non-Dial admins pass straight through. */}
                <GlobalCallLayer>
                  <Outlet />
                </GlobalCallLayer>
              </>
            </Suspense>
          </SessionBoundary>
        }
      >
        <Route index element={<AdminSearchRoute />} />
        <Route
          path="cohorts"
          element={
            <MenuLayout currentTab="cohorts">
              <AdminLayout>
                <CohortSelectScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="cohorts/:id"
          element={
            <MenuLayout currentTab="cohorts">
              <CohortScreen />
            </MenuLayout>
          }
        />
        <Route
          path="referrals"
          element={
            <MenuLayout currentTab="referrals">
              <AdminLayout>
                <AdminReferralsScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        {/* Renamed to /admin/referrals; keeps existing bookmarks working. */}
        <Route
          path="partners"
          element={<Navigate to={`${ADMIN_PATH}/referrals`} replace />}
        />
        <Route
          path="student-payments"
          element={
            <MenuLayout currentTab="reimbursements">
              <AdminLayout>
                <ReimbursementReviewScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="ai/*"
          element={
            <MenuLayout currentTab="ai_console">
              <AdminLayout>
                <AiConsolePage />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="student-payments/analytics"
          element={
            <MenuLayout currentTab="reimbursements">
              <AdminLayout>
                <ReimbursementAnalyticsScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="student-payments/ai-analysis"
          element={
            <MenuLayout currentTab="reimbursements">
              <AdminLayout>
                <ReimbursementAIAnalysisScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="student-payments/financial-aid"
          element={
            <MenuLayout currentTab="reimbursements">
              <AdminLayout>
                <FinancialAidReviewScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="student-payments/financial-aid/analytics"
          element={
            <MenuLayout currentTab="reimbursements">
              <AdminLayout>
                <FinancialAidAnalyticsScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="billing/*"
          element={
            <MenuLayout currentTab="billing">
              <AdminLayout>
                <BillingScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="success-queue/:caseId?"
          element={
            <CaseManagementGate>
              <ThemeProvider theme={successQueueTheme}>
                <MenuLayout currentTab="success_queue">
                  <SuccessQueueScreen />
                </MenuLayout>
              </ThemeProvider>
            </CaseManagementGate>
          }
        />
        <Route
          path="dial/students/:caseId?"
          element={
            <CaseManagementGate>
              <ThemeProvider theme={successQueueTheme}>
                <MenuLayout currentTab="success_queue">
                  <SuccessQueueScreen />
                </MenuLayout>
              </ThemeProvider>
            </CaseManagementGate>
          }
        />
        <Route
          path="dial/outreach"
          element={
            <CaseManagementGate>
              <ThemeProvider theme={successQueueTheme}>
                <MenuLayout currentTab="success_queue">
                  <OutreachDialScreen />
                </MenuLayout>
              </ThemeProvider>
            </CaseManagementGate>
          }
        />
        <Route
          path="dial/free"
          element={
            <CaseManagementGate>
              <ThemeProvider theme={successQueueTheme}>
                <MenuLayout currentTab="success_queue">
                  <FreeDialScreen />
                </MenuLayout>
              </ThemeProvider>
            </CaseManagementGate>
          }
        />
        <Route
          path="super-admin"
          element={
            <MenuLayout currentTab="superadmin">
              <AdminLayout>
                <SuperAdminScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="new-vocations"
          element={
            <MenuLayout currentTab="new_vocations">
              <AdminLayout>
                <NewVocationsScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="job-search-analytics"
          element={
            <MenuLayout currentTab="job_search_analytics">
              <AdminLayout>
                <JobSearchAnalyticsScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="sales-enablement"
          element={
            <MenuLayout currentTab="sales_enablement">
              <AdminLayout>
                <SalesEnablementScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="media-library"
          element={
            <MenuLayout currentTab="media_library">
              <AdminLayout>
                <MediaLibraryScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="skills-training-analytics"
          element={
            <MenuLayout currentTab="skills_training_analytics">
              <AdminLayout>
                <SkillsTrainingAnalyticsScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="online-training-analytics"
          element={
            <MenuLayout currentTab="online_training_analytics">
              <AdminLayout>
                <OnlineTrainingAnalyticsScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="document-ai-analysis"
          element={
            <MenuLayout currentTab="document_ai_analytics">
              <AdminLayout>
                <DocumentAIAnalysisScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="content"
          element={
            <MenuLayout currentTab="content">
              <AdminLayout>
                <ContentDashboardScreen />
              </AdminLayout>
            </MenuLayout>
          }
        />
        <Route
          path="curriculum/:vocationId"
          element={
            <MenuLayout currentTab="curriculum">
              <CurriculumScreen />
            </MenuLayout>
          }
        />
        <Route
          path="curriculum/:vocationId/:atomId"
          element={
            <MenuLayout currentTab="curriculum">
              <CurriculumScreen />
            </MenuLayout>
          }
        />
        <Route
          path="changelog"
          element={
            <MenuLayout currentTab="changelog">
              <ChangelogScreen />
            </MenuLayout>
          }
        />
        {/* Full-bleed dark KPI board (no nav chrome) — synced from the company sheet */}
        <Route path="scoreboard" element={<ScoreboardScreen />} />
        <Route
          path="clients/:id"
          element={
            <MenuLayout currentTab="clients">
              <ClientScreen />
            </MenuLayout>
          }
        />
      </Route>
    </Route>
  );
}
